import { useEffect, useMemo, useRef, useState } from 'react';

import { useReactTable, getCoreRowModel } from '@tanstack/react-table';
import { isEmpty } from 'lodash';

import { getColumnsDefinitions, getColumnsVisibility } from './columnAdapter';

/**
 * @typedef IPage
 * @property { number } pageNumber
 * @property { number } pageSize
 * @property { number } offset
 */

/**
 * @typedef IPagination
 * @property { number } pageNumber
 * @property { number } [totalRows]
 * @property { number} pageSize
 */

/**
 * @template D
 * @typedef { (rowData: D, index: number) => import('react').ReactNode | (text: string | boolean | number, rowData: D, index: number) => import('react').ReactNode } CellRenderer
 */

/**
 * @typedef { Object } ITableColumn
 * @property { React.ReactNode } title
 * @property { string } id
 * @property { string } [dataKey]
 * @property { CellRenderer } [renderer]
 * @property { React.ThHTMLAttributes<HTMLTableCellElement> } [attributes]
 * @property { boolean } [ellipsis]
 * @property { "left" | "center" | "right" } [align]
 * @property { string } [cellClassName]
 */

/**
 * @template D
 * @typedef { Object } ITableProps
 * @property { ITableColumn[] } columns
 * @property { Array<D> } data
 * @property { string } rowUniqueKey
 * @property { number } [rowHeight] - row height in `px`
 * @property { string } [rowClassName]
 * @property { string } [columnClassName]
 * @property { string } [className]
 * @property { IPagination } [pagination]
 * @property { string[] } [hideColumns]
 * @property { boolean } [loading]
 * @property { React.ReactNode } [loader]
 * @property { boolean } [scrollable]
 * @property { (page: IPage) => void } [onPageChange]
 * @property { (rowData: D, index: number) => void } [onRowClick]
 * @property { { [icon: string]; text: ReactNode } } [emptyView]
 */

/**
 * @template D
 * @param { Object } params
 * @param { ITableProps<D>['data'] } params.data
 * @param { ITableProps<D>['columns'] } params.columns
 * @param { ITableProps<D>['hideColumns'] } params.hideColumns
 * @param { ITableProps<D>['pagination'] } params.pagination
 * @param { ITableProps<D>['onPageChange'] } params.onPageChange
 */
export const useDataTable = ({
	data,
	columns,
	hideColumns,
	pagination = {},
	onPageChange,
}) => {
	const [showScrollShadow, setShowScrollShadow] = useState(false);
	const { pageSize = 10, totalRows = -1, pageNumber = 1 } = pagination;
	const shouldPaginate = !isEmpty(pagination) && totalRows > pageSize;
	const tableContainer = useRef();

	const columsDefinitions = useMemo(() => {
		return getColumnsDefinitions(columns);
	}, [columns]);

	const columnVisibility = useMemo(() => {
		return getColumnsVisibility(hideColumns);
	}, [hideColumns]);

	useEffect(() => {
		if (tableContainer.current) {
			const { scrollWidth, clientWidth } = tableContainer.current;
			setShowScrollShadow(scrollWidth > clientWidth);
		}
	}, [hideColumns]);

	function changePage(pageNumber, pageSize) {
		onPageChange({
			pageNumber,
			pageSize,
			offset: (pageNumber - 1) * pageSize,
		});
	}

	const table = useReactTable({
		data,
		columns: columsDefinitions,
		state: {
			columnVisibility,
		},
		getCoreRowModel: getCoreRowModel(),
	});

	return {
		table,
		shouldPaginate,
		pageNumber,
		totalRows,
		pageSize,
		changePage,
		showScrollShadow,
		tableContainer,
	};
};
